/*
 * What it takes to bill a partner and what it takes to pay them.
 *
 * Two panels, because they are two jobs done at different times by different people: BILLING
 * builds the Zoho customer and prints on the invoice, PAYOUTS is where the money is actually
 * sent. A single list of thirteen fields would have to be re-read in full each time to find the
 * four that matter today.
 *
 * All of it was already on the `/api/admin/partners` response and rendered nowhere, so the way
 * to find a partner's GSTIN was to open the database.
 *
 * ADMINS CAN EDIT IT, because some of this arrives on a call or by email and never gets typed
 * into the portal, and until it does the partner cannot be billed or paid. Both save through
 * the SAME backend path the partner's own Profile uses, so an admin is not a way around the
 * validation, the fraud audit or the notice the partner gets. Changing a destination here still
 * warns the partner, still writes the audit row, and now names the admin on both.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

export const ENTITY_TYPES = {
  individual: 'Individual / sole proprietor',
  partnership: 'Partnership firm',
  llp: 'Limited liability partnership',
  private_limited: 'Private limited company',
  public_limited: 'Public limited company',
  trust_society: 'Trust or society',
};

// `missing_particulars` returns field names, never sentences — the backend decides what is
// missing, the screen decides how to say it.
const MISSING_LABELS = {
  entity_type: 'entity type',
  legal_name: 'legal name',
  registered_address: 'registered address',
  signatory_designation: 'signatory designation',
  pan_no: 'PAN',
};

const Field = ({ id, label, value, onChange, mono, placeholder }) => (
  <div>
    <Label htmlFor={id} className="text-xs text-[#64748B]">{label}</Label>
    <Input
      id={id}
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`mt-1 ${mono ? 'font-mono text-xs' : ''}`}
      data-testid={`paperwork-${id}`}
    />
  </div>
);

const Panel = ({ title, note, children, onSave, dirty, saving }) => (
  <div className="rounded-lg border border-[#E2E2DF] p-4">
    <p className="text-xs uppercase tracking-wider text-[#64748B]">{title}</p>
    <p className="mt-1 text-xs text-[#64748B]">{note}</p>
    <div className="mt-3 space-y-3">{children}</div>
    <Button
      size="sm"
      className="mt-4"
      onClick={onSave}
      disabled={!dirty || saving}
      data-testid={`save-${title.toLowerCase()}`}
    >
      {saving ? 'Saving…' : `Save ${title.toLowerCase()}`}
    </Button>
  </div>
);

const PartnerPaperwork = ({ partner, apiUrl, headers, onSaved }) => {
  const stored = useMemo(() => {
    const p = partner.particulars || {};
    return {
      entity_type: p.entity_type || '',
      legal_name: p.legal_name || '',
      registered_address: p.registered_address || partner.address || '',
      signatory_designation: p.signatory_designation || '',
      gstin: partner.gstin || '',
      pan_no: partner.pan_no || '',
      place_of_supply: partner.place_of_supply || '',
      company_name_bank: partner.company_name_bank || '',
      bank_account: partner.bank_account || '',
      ifsc_code: partner.ifsc_code || '',
      upi_id: partner.upi_id || '',
    };
  }, [partner]);

  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState('');

  // An admin switching partners inside the same dialog would otherwise be editing the last
  // one's paperwork over this one's.
  useEffect(() => setDraft(stored), [stored]);

  const set = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
  const changed = (fields) => fields.some((f) => (draft[f] || '') !== (stored[f] || ''));

  const missing = partner.missing_particulars || [];
  // UPI or a bank account will do. A partner needs one route for the money, not both.
  const payable = Boolean(stored.upi_id || (stored.bank_account && stored.ifsc_code));

  const BILLING = ['entity_type', 'legal_name', 'registered_address', 'signatory_designation',
    'gstin', 'pan_no', 'place_of_supply'];
  const PAYOUT = ['company_name_bank', 'bank_account', 'ifsc_code', 'upi_id'];

  const saveBilling = async () => {
    setSaving('billing');
    try {
      // The particulars route writes GSTIN, PAN and place of supply onto the user record too —
      // it is the one form for a partner's legal identity, not a second copy of it.
      const { data } = await axios.put(
        `${apiUrl}/api/admin/partners/${partner.id}/particulars`,
        {
          entity_type: draft.entity_type,
          legal_name: draft.legal_name,
          registered_address: draft.registered_address,
          signatory_designation: draft.signatory_designation,
          gstin: draft.gstin,
          pan_no: draft.pan_no,
          place_of_supply: draft.place_of_supply,
        },
        { headers },
      );
      toast.success(
        data.agreement_issued
          ? 'Saved, and their agreement has been issued'
          : data.missing?.length
            ? `Saved. Still missing: ${data.missing.map((m) => MISSING_LABELS[m] || m).join(', ')}`
            : 'Billing details saved'
      );
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save those details');
    } finally {
      setSaving('');
    }
  };

  const savePayout = async () => {
    setSaving('payouts');
    try {
      await axios.put(
        `${apiUrl}/api/admin/partners/${partner.id}/payout-profile`,
        {
          company_name_bank: draft.company_name_bank,
          bank_account: draft.bank_account,
          ifsc_code: draft.ifsc_code,
          upi_id: draft.upi_id,
        },
        { headers },
      );
      toast.success('Payout details saved. The partner has been notified');
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save those details');
    } finally {
      setSaving('');
    }
  };

  return (
    <div className="space-y-3 border-t border-[#E2E2DF] pt-4" data-testid="partner-paperwork">
      {missing.length > 0 && (
        <p className="rounded-md border border-[#fdba74] bg-[#fff7ed] px-3 py-2 text-xs text-[#9a4a06]">
          Not billable yet. Still missing: {missing.map((m) => MISSING_LABELS[m] || m).join(', ')}.
        </p>
      )}
      {!payable && (
        <p className="rounded-md border border-[#fdba74] bg-[#fff7ed] px-3 py-2 text-xs text-[#9a4a06]">
          No way to pay this partner yet. Neither a UPI ID nor a full bank account with IFSC.
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel
          title="Billing"
          note="What their Zoho customer and every invoice are built from."
          onSave={saveBilling}
          dirty={changed(BILLING)}
          saving={saving === 'billing'}
        >
          <div>
            <Label htmlFor="entity_type" className="text-xs text-[#64748B]">Constituted as</Label>
            <Select value={draft.entity_type} onValueChange={(v) => set('entity_type', v)}>
              <SelectTrigger id="entity_type" className="mt-1" data-testid="paperwork-entity_type">
                <SelectValue placeholder="Not provided" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ENTITY_TYPES).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field id="legal_name" label="Legal name" value={draft.legal_name}
                 onChange={(v) => set('legal_name', v)} />
          <Field id="registered_address" label="Registered address" value={draft.registered_address}
                 onChange={(v) => set('registered_address', v)} />
          <Field id="signatory_designation" label="Signs as"
                 placeholder="Director, Partner, Trustee"
                 value={draft.signatory_designation}
                 onChange={(v) => set('signatory_designation', v)} />
          <Field id="gstin" label="GSTIN" mono value={draft.gstin}
                 onChange={(v) => set('gstin', v.toUpperCase())} />
          <Field id="pan_no" label="PAN" mono value={draft.pan_no}
                 onChange={(v) => set('pan_no', v.toUpperCase())} />
          <Field id="place_of_supply" label="Place of supply" value={draft.place_of_supply}
                 onChange={(v) => set('place_of_supply', v)} />
        </Panel>

        <Panel
          title="Payouts"
          note="Where the money actually goes. Saving this warns the partner, naming us."
          onSave={savePayout}
          dirty={changed(PAYOUT)}
          saving={saving === 'payouts'}
        >
          <Field id="company_name_bank" label="Account name" value={draft.company_name_bank}
                 onChange={(v) => set('company_name_bank', v)} />
          <Field id="bank_account" label="Account number" mono value={draft.bank_account}
                 onChange={(v) => set('bank_account', v)} />
          <Field id="ifsc_code" label="IFSC" mono value={draft.ifsc_code}
                 onChange={(v) => set('ifsc_code', v.toUpperCase())} />
          <Field id="upi_id" label="UPI" mono value={draft.upi_id}
                 onChange={(v) => set('upi_id', v)} />
          <div className="pt-1 text-xs text-[#64748B]">
            <p>{partner.email}</p>
            <p>{partner.mobile}</p>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default PartnerPaperwork;
